// Task 16: lineage drill-down modal.
// Consumes GET /lifecycle/:type/:id (Task 12) via the api lib. The deployed
// handler's response is `{ object, children, events }` (asserted field-for-field
// by api/src/routes/lifecycle.route.test.ts); pre-redeploy briefs called the
// root node `node`, so both spellings are tolerated when reading it.
// The remote Worker 404s this endpoint until its redeploy: the modal degrades
// to an explanatory empty state instead of a broken modal.
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { mapLegacy, stageOf, type Stage } from '../lib/lifecycleStepper';
import LifecycleStepper, { stageLabel } from './LifecycleStepper';
import Tooltip from './Tooltip';

export interface LifecycleDrilldownProps {
  type: 'bank_statement' | 'card_statement' | 'invoice' | 'journal_entry';
  id: string | null;
  open: boolean;
  onClose: () => void;
}

interface LifecycleNode {
  id: string;
  type: string;
  status: string;
  stage: string;
  stage_since: string | null;
  flags: string[];
}
interface LifecycleChild { id: string; entry_number: string; stage: string; temp_parked: boolean }
interface LifecycleEvent {
  at: string;
  actor: string;
  action: string;
  from: string | null;
  to: string | null;
  reason: string | null;
}
interface DrilldownResponse {
  object?: LifecycleNode;
  node?: LifecycleNode;
  children?: LifecycleChild[];
  events?: LifecycleEvent[];
}

const TYPE_LABELS: Record<string, [string, string, string]> = {
  bank_statement: ['Bank statement', '銀行月結單', '银行月结单'],
  card_statement: ['Card statement', '信用卡月結單', '信用卡月结单'],
  invoice: ['Invoice', '發票', '发票'],
  journal_entry: ['Journal entry', '日記帳分錄', '日记账分录'],
};

const FLAG_LABELS: Record<string, [string, string, string]> = {
  draft: ['Draft', '草稿', '草稿'],
  balance_mismatch: ['Balance', '餘額', '余额'],
  direction: ['Direction', '方向', '方向'],
  company_not_detected: ['Company', '公司', '公司'],
  duplicate: ['Duplicate', '重複', '重复'],
  total: ['Total mismatch', '總額不符', '总额不符'],
  pending_review: ['Needs review', '待審核', '待审核'],
  new_company: ['New company', '新公司', '新公司'],
  temporary_ar_ap: ['Parked in temp', '暫記科目', '暂记科目'],
};

const ACTION_LABELS: Record<string, [string, string, string]> = {
  lifecycle: ['State change', '狀態變更', '状态变更'],
  status: ['Status', '狀態', '状态'],
  review_confirm: ['Review confirmed', '覆核確認', '复核确认'],
  review_statement: ['Statement reviewed', '月結單已覆核', '月结单已复核'],
  confirm: ['Confirmed', '已確認', '已确认'],
  confirm_match: ['Match confirmed', '配對已確認', '配对已确认'],
  unlink: ['Unlinked', '已解除', '已解除'],
  unlink_match: ['Match unlinked', '配對已解除', '配对已解除'],
  create: ['Created', '建立', '建立'],
  update: ['Edited', '編輯', '编辑'],
  import: ['Imported', '匯入', '汇入'],
  send_back: ['Sent back', '已退回', '已退回'],
  soft_delete: ['Moved to recycle bin', '已移至回收桶', '已移至回收桶'],
  restore: ['Restored', '已還原', '已还原'],
  purge: ['Purged', '已永久刪除', '已永久删除'],
};

type Triple = [string, string, string];
const pick = (t?: Triple): string => (t ? tr(t[0], t[1], t[2]) : '');

function flagLabel(flag: string): string {
  return pick(FLAG_LABELS[flag]) || flag;
}

function actionLabel(action: string): string {
  return pick(ACTION_LABELS[action]) || action;
}

const STAGE_KEYS = new Set<string>([
  'uploaded', 'extracting', 'extracted', 'needs_review', 'reviewed',
  'booked', 'posted', 'reconciled', 'settled', 'voided',
]);

/** Localized from/to for the event arrows; pre-migration literals map too. */
function sideLabel(v: string | null | undefined): string {
  if (!v) return '';
  if (STAGE_KEYS.has(v)) return stageLabel(v as Stage);
  const doc = mapLegacy('document', v);
  if (doc) return stageLabel(doc);
  const vch = mapLegacy('voucher', v);
  if (vch) return stageLabel(vch);
  return v;
}

const fmtAt = (at: string | null) => (at || '').slice(0, 16).replace('T', ' ');

export default function LifecycleDrilldown({ type, id, open, onClose }: LifecycleDrilldownProps) {
  const { i18n } = useTranslation();

  const q = useQuery<DrilldownResponse>({
    // Canonical ['lifecycle', type, id] namespace (brief-mandated): Task 17's
    // send-back flow invalidates ['lifecycle', ...] and must reach this query.
    queryKey: ['lifecycle', type, id],
    queryFn: () => api(`/lifecycle/${type}/${id}`),
    enabled: open && !!id,
  });

  if (!open) return null;

  const node = q.data?.object ?? q.data?.node ?? null;
  const children = q.data?.children ?? [];
  const events = q.data?.events ?? [];
  const nodeLane = type === 'journal_entry' ? 'voucher' : 'document';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="lifecycle-drilldown">
      <div className="bg-card border rounded-xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <h3 className="font-bold text-base">{pick(TYPE_LABELS[type])}</h3>
          <span className="text-xs text-muted-foreground">{tr('Lifecycle drill-down', '生命週期檢視', '生命周期视图')}</span>
          <button onClick={onClose} className="ml-auto p-1 hover:bg-muted rounded" aria-label={tr('Close', '關閉', '关闭')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 space-y-4 p-4">
          {q.isLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          )}

          {!q.isLoading && q.isError && (
            <div className="bg-card border rounded-xl p-10 text-center space-y-3">
              <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
              <p className="text-lg font-semibold">
                {tr('Lineage not available yet', '歷程記錄尚未開放', '历程记录尚未开放')}
              </p>
              <p className="text-sm text-muted-foreground">
                {tr(
                  'The deployed API build does not expose document lineage yet. This view fills in once the backend is redeployed.',
                  '目前部署的 API 版本尚未提供文檔歷程記錄。後端重新部署後即可在此顯示。',
                  '目前部署的 API 版本尚未提供文档历程记录。后端重新部署后即可在此显示。'
                )}
              </p>
            </div>
          )}

          {!q.isLoading && !q.isError && node && (
            <>
              {/* Object node: full stepper + status + its own flag chips */}
              <div className="border rounded-lg p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                  {tr('Object', '對象', '对象')}
                </p>
                <LifecycleStepper
                  type={nodeLane as 'document' | 'voucher'}
                  variant="full"
                  stage={stageOf(nodeLane as 'document' | 'voucher', {
                    lifecycle: { stage: node.stage },
                    status: node.status,
                  })}
                  stageSince={node.stage_since ?? undefined}
                />
                <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px] text-muted-foreground">
                  <span className="font-mono">{node.id}</span>
                  {node.flags.map(f => (
                    <span key={f} className="text-[10px] bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 px-1.5 py-0.5 rounded-full font-medium">
                      {flagLabel(f)}
                    </span>
                  ))}
                </div>
              </div>

              {/* Child vouchers: entry_number + mini stepper + temp-park badge */}
              <div className="border rounded-lg p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                  {tr('Linked vouchers', '連結的過賬分錄', '关联的过账分录')}
                </p>
                {children.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{tr('No vouchers yet', '尚無過賬分錄', '尚无过账分录')}</p>
                ) : (
                  <ul className="space-y-1.5">
                    {children.map(ch => (
                      <li key={ch.id} className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-mono font-semibold">{ch.entry_number}</span>
                        <LifecycleStepper
                          type="voucher"
                          variant="compact"
                          stage={stageOf('voucher', { lifecycle: { stage: ch.stage } })}
                        />
                        {ch.temp_parked && (
                          <Tooltip
                            content={
                              <span className="whitespace-nowrap text-xs">
                                {flagLabel('temporary_ar_ap')}
                                {' — '}
                                {tr(
                                  'One side of this entry sits in a temporary parking account (11901 Temporary Receipt 暫收款 / 21901 Temporary Payment 暫付款).',
                                  '分錄其中一方暫存於暫記科目（11901 暫收款／21901 暫付款）。',
                                  '分录其中一方暂存于暂记科目（11901 暂收款／21901 暂付款）。'
                                )}
                              </span>
                            }
                          >
                            <span className="text-[10px] bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 px-1.5 py-0.5 rounded-full font-medium cursor-help">
                              {flagLabel('temporary_ar_ap')}
                            </span>
                          </Tooltip>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Events timeline: at · actor · from→to · reason (newest first) */}
              <div className="border rounded-lg p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                  {tr('History', '歷史', '历史')}
                </p>
                {events.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{tr('No recorded events', '沒有記錄事件', '没有记录事件')}</p>
                ) : (
                  <ul className="space-y-0">
                    {events.map((ev, i) => (
                      <li
                        key={`${ev.at}-${i}`}
                        className="relative pl-4 pb-2.5 text-xs before:absolute before:left-[3px] before:top-[6px] before:h-2 before:w-2 before:rounded-full before:bg-border"
                      >
                        <span className="font-medium">{actionLabel(ev.action)}</span>
                        {(ev.from || ev.to) && (
                          <span className="ml-1.5">
                            {`${ev.from ? sideLabel(ev.from) : '—'} → ${ev.to ? sideLabel(ev.to) : '—'}`}
                          </span>
                        )}
                        <span className="text-muted-foreground text-[11px] ml-1.5">
                          {fmtAt(ev.at)}{' · '}{ev.actor}
                        </span>
                        {ev.reason && (
                          <span className="text-muted-foreground italic ml-1.5">“{ev.reason}”</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
